import type { ConnectorUsageReportType } from "../../shared/connectorUsageReporting";
import type { QueryRowType } from "@rocicorp/zero";
import {
  connectorsByIdentifier,
  connectorTypeOf,
  connectorTypes,
} from "../../shared/connectors";
import type { ConnectorWithOAuth } from "../../shared/connectors/getTargetValue";
import type { IconName } from "../components/Icon";
import type { queries } from "../../shared/queries";
import { domainFromUrlPattern } from "../../shared/connectors/urlDomain";

export type Connector = ConnectorWithOAuth;

export type ConnectorInfo = QueryRowType<
  ReturnType<typeof queries.connectorCatalog>
>;

// A product's display name from its connector identifier.
const productLabel = (key: string) => connectorsByIdentifier[key]?.name ?? key;

const exampleDomains = new Set(["example.com", "example.net", "example.org"]);

// Products we can auto-detect for a custom connector: real branded connectors,
// not the generic custom shells (apiKey, customOauth, smtp, …).
const productByUrlPattern = new Map<string, string>();
const productByDomain = new Map<string, string>();
for (const type of connectorTypes) {
  if (type.tags.includes("custom")) continue;
  for (const variant of type.variants) {
    const urlPattern = variant.defaultUrlPattern({}).replace(/^\^/, "");
    const domain = domainFromUrlPattern(urlPattern);
    if (!domain || exampleDomains.has(domain)) continue;
    if (!productByUrlPattern.has(urlPattern))
      productByUrlPattern.set(urlPattern, type.identifier);
    if (!productByDomain.has(domain))
      productByDomain.set(domain, type.identifier);
  }
}

// Only the generic custom shells get their product detected from the URL; every
// other connector already declares what it is through its identifier.
const inferableIdentifiers = new Set(["apiKey", "customOauth", "remoteMcp"]);

function inferProduct(urlPattern: string | undefined): string | null {
  const domain = urlPattern ? domainFromUrlPattern(urlPattern) : null;
  if (!urlPattern || !domain) return null;
  return (
    productByUrlPattern.get(urlPattern.replace(/^\^/, "")) ??
    productByDomain.get(domain) ??
    null
  );
}

// A connector's product when it isn't its own: detected from the URL domain
// for the generic custom shells, declared by the definition for variants of
// another product (e.g. an MCP twin). Null when the connector stands for its
// product itself or nothing is known.
export function resolveProduct(item: {
  identifier: string;
  urlPattern?: string;
}): string | null {
  if (inferableIdentifiers.has(item.identifier))
    return inferProduct(item.urlPattern);
  const type = connectorTypeOf(item.identifier);
  return type && type.identifier !== item.identifier ? type.identifier : null;
}

// The connector's own type, ignoring any URL-detected product — so a custom
// connector grouped under Slack still reads as "API key".
export function connectorRawTypeLabel(app: {
  identifier: string;
  oauthConnection?: unknown;
}): string {
  return (
    connectorsByIdentifier[app.identifier]?.name ??
    (app.oauthConnection ? "OAuth" : "API key")
  );
}

export function connectorTypeKey(app: {
  identifier: string;
  urlPattern?: string;
}): string {
  return resolveProduct(app) ?? app.identifier;
}

export function connectorTypeLabel(app: {
  identifier: string;
  urlPattern?: string;
  oauthConnection?: unknown;
}): string {
  const resolved = resolveProduct(app);
  if (resolved) return productLabel(resolved);
  return connectorRawTypeLabel(app);
}

// The connectors-list search contract: a connector matches on its name or its
// type label, so "MCP" finds every MCP server whatever it's named.
export function connectorMatchesQuery(
  app: {
    name: string;
    identifier: string;
    urlPattern?: string;
    oauthConnection?: unknown;
  },
  lowerQuery: string
): boolean {
  return (
    app.name.toLowerCase().includes(lowerQuery) ||
    connectorTypeLabel(app).toLowerCase().includes(lowerQuery)
  );
}

// How the connector authenticates, in the variant picker's vocabulary — so a
// product's default variant reads “User OAuth”, not the bare product name.
export function connectorAuthTypeLabel(app: {
  identifier: string;
  oauthConnection?: unknown;
}): string {
  return (
    connectorsByIdentifier[app.identifier]?.authLabel ??
    connectorRawTypeLabel(app)
  );
}

export function connectorNameWithAuth(app: {
  name: string;
  identifier: string;
  oauthConnection?: unknown;
}): string {
  const auth = connectorAuthTypeLabel(app);
  return auth === app.name ? app.name : `${app.name} · ${auth}`;
}

type BranchConnectors =
  | {
      headConnectorUsages: readonly { connectorId: string }[];
    }
  | { headCommitId: string | null };

export function commitConnectorIds(
  branch: BranchConnectors | undefined
): string[] {
  return branch && "headConnectorUsages" in branch
    ? branch.headConnectorUsages.map((usage) => usage.connectorId)
    : [];
}

// A workflow's live connectors: those at the head of its main branch.
export function mainBranchConnectorIds(
  branches: readonly (BranchConnectors & { name: string })[]
): string[] {
  const main = branches.find((b) => b.name === "main") ?? branches[0];
  return commitConnectorIds(main);
}

export const usageTypeIcons: Record<
  | "workflow"
  | ConnectorUsageReportType<"list">
  | ConnectorUsageReportType<"details">,
  IconName
> = {
  workflow: "product-workflows",
  personal_chat: "ask",
  space_git_remote: "git-branch",
  skill_git_source: "skills",
  audit_log_destination: "output",
  ai_provider: "brain",
};

type GrantLike = {
  scopeId: string;
  builtinRoleId: string | null;
  customRoleId: string | null;
};

type PrincipalHolder = { roleGrantsAsPrincipal: readonly GrantLike[] };

type AppAccessResult =
  | {
      roleGrantsAsScope: readonly {
        userGroup?: PrincipalHolder | undefined;
        user?: PrincipalHolder | undefined;
      }[];
    }
  | null
  | undefined;

export function collectPrincipalGrants(
  connectorAccess: AppAccessResult
): GrantLike[] {
  return (
    connectorAccess?.roleGrantsAsScope.flatMap((g) => [
      ...(g.userGroup?.roleGrantsAsPrincipal ?? []),
      ...(g.user?.roleGrantsAsPrincipal ?? []),
    ]) ?? []
  );
}

export type LiveWorkflow = {
  id: string;
  name: string;
  badgeSeed: string | null;
  spaceId: string;
  draft: boolean;
};

type LiveUsageRow = QueryRowType<
  ReturnType<typeof queries.connectorUsageDetails>
>;
type UsageSpace = NonNullable<LiveUsageRow["space"]>;

type StepInfo = {
  stepDir: string;
  workflowId: string;
  workflowName: string;
  spaceId: string;
};

type StepActivityRow = LiveUsageRow["stepActivity"][number];

type BranchUsage = {
  branchId: string;
  branchName: string;
  steps: StepInfo[];
  stepActivity: readonly StepActivityRow[];
  total: number;
};

export type WorkflowUsage = {
  workflowId: string;
  workflowName: string;
  spaceId: string;
  badgeSeed: string | null;
  branches: readonly { name: string }[];
  space: UsageSpace;
  usageBranches: BranchUsage[];
};

const mainFirst = (a: BranchUsage, b: BranchUsage) =>
  a.branchName === "main"
    ? -1
    : b.branchName === "main"
      ? 1
      : a.branchName.localeCompare(b.branchName);

/** One group per workflow, with its usages bucketed per branch (live first). */
export function groupByWorkflow(
  rows: readonly LiveUsageRow[]
): WorkflowUsage[] {
  const groups: WorkflowUsage[] = [];
  for (const wf of rows) {
    if (!wf.space) continue;
    const usageBranches: BranchUsage[] = [];
    for (const usage of wf.connectorUsages) {
      const branch = wf.branches.find((b) => b.id === usage.branchId);
      if (!branch) continue;
      const stepActivity = wf.stepActivity.filter(
        (a) => a.branchId === branch.id
      );
      const countByDir = new Map(
        stepActivity.map((a) => [a.stepDir, a.executionCount ?? 0])
      );
      const steps = usage.stepDirs
        .map((stepDir) => ({
          stepDir,
          workflowId: wf.id,
          workflowName: wf.name,
          spaceId: wf.spaceId,
        }))
        .sort(
          (a, b) =>
            (countByDir.get(b.stepDir) ?? 0) -
              (countByDir.get(a.stepDir) ?? 0) ||
            a.stepDir.localeCompare(b.stepDir)
        );
      usageBranches.push({
        branchId: branch.id,
        branchName: branch.name,
        steps,
        stepActivity,
        total: steps.reduce(
          (sum, s) => sum + (countByDir.get(s.stepDir) ?? 0),
          0
        ),
      });
    }
    if (usageBranches.length === 0) continue;
    groups.push({
      workflowId: wf.id,
      workflowName: wf.name,
      spaceId: wf.spaceId,
      badgeSeed: wf.badgeSeed,
      branches: wf.branches,
      space: wf.space,
      usageBranches: usageBranches.sort(mainFirst),
    });
  }
  const liveTotal = (g: WorkflowUsage) => g.usageBranches[0]?.total ?? 0;
  return groups.sort(
    (a, b) =>
      liveTotal(b) - liveTotal(a) ||
      a.workflowName.localeCompare(b.workflowName)
  );
}

export function liveWorkflows(
  rows: readonly LiveUsageRow[]
): Array<LiveWorkflow> {
  return rows.flatMap((wf) => {
    const using = wf.branches.filter((b) =>
      wf.connectorUsages.some((u) => u.branchId === b.id)
    );
    if (using.length === 0) return [];
    return [
      {
        id: wf.id,
        name: wf.name,
        badgeSeed: wf.badgeSeed,
        spaceId: wf.spaceId,
        draft: !using.some((b) => b.name === "main"),
      },
    ];
  });
}

// Split a step's connectors into the ones that get a live, manageable chip and
// the ones that need the faded "reconnect" treatment (missing or unreadable).
export function splitStepConnectors<
  App extends { id: string },
  Row extends { connectedApp: { id: string } | null; connectedAppId: string },
>(
  stepConnectors: readonly Row[],
  allConnectors: readonly App[]
): { connected: App[]; associatedIds: string[]; disconnected: Row[] } {
  const associatedIdSet = new Set(stepConnectors.map((r) => r.connectedAppId));
  const connected: App[] = [];
  const associatedIds: string[] = [];
  for (const app of allConnectors) {
    if (!associatedIdSet.has(app.id)) continue;
    associatedIds.push(app.id);
    connected.push(app);
  }
  const disconnected = stepConnectors.filter((r) => !r.connectedApp);
  return { connected, associatedIds, disconnected };
}

export const connectorGroupKey = (item: {
  identifier: string;
  name: string;
  urlPattern?: string;
}) => {
  const resolved = resolveProduct(item);
  if (resolved) return resolved;
  return inferableIdentifiers.has(item.identifier)
    ? `${item.identifier}-${item.name}`
    : item.identifier;
};

export const uniqueByType = <T extends { identifier: string; name: string }>(
  list: T[]
) => {
  const seen: string[] = [];
  const result: T[] = [];
  list.forEach((item) => {
    const reference = connectorGroupKey(item);
    if (!seen.includes(reference)) {
      seen.push(reference);
      result.push(item);
    }
  });
  return result;
};

// The ConnectorAvatars `connected` items for a set of connected-app ids:
// resolve each against the index, drop unknown ids, and collapse to one avatar
// per connector type.
export function connectorAvatarItems(
  ids: readonly string[],
  connectorById: ReadonlyMap<string, ConnectorInfo>
): { id: string; name: string; app: ConnectorInfo }[] {
  return uniqueByType(
    ids
      .map((id) => connectorById.get(id))
      .filter((ca): ca is ConnectorInfo => ca != null)
  ).map((ca) => ({ id: ca.id, name: ca.name, app: ca }));
}

export function aiModelConnectorNames(
  connectedApps: readonly { appType: string }[]
): string[] {
  return [
    ...new Set(
      connectedApps.flatMap(({ appType }) => {
        const definition = connectorsByIdentifier[appType];
        return definition?.usage ? [definition.name] : [];
      })
    ),
  ];
}
